import { get, chain, isEqual, some, find, pick, set } from "lodash"
import { createSelector } from "reselect"
import { OK } from "helpers/data-statuses"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getExperiment as getOptimizeExperiment } from "modules/optimization/selectors/trades"
import { getExperiment as getNewSimulationExperiment } from "modules/rebalance/selectors/data"
import { getExperiment as getOldSimulationExperiment } from "modules/rebalance/selectors/old-experiments"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { convertExperimentChangesToSimulateActions, formatPositionId } from "helpers/simulator"
import { getSelectedDate } from "./dates"
import { buildContributorKey, combinePositionsIncludingSplitBasket } from "helpers/contributors"
import { getSimulateActions } from "modules/rebalance/selectors"

const getExperimentPositions = state => get(state, "global.data.experimentPositions.data.model.simulation.composition[0].positions")
const getPortfolioPositions = state => get(state, "global.data.portfolioPositions.data.model.nonActive.composition[0].positions")
const getPortfolioGmv = state => get(state, "global.data.portfolioPositions.data.model.nonActive.composition[0].gmv", 0)
const getPortfolioEquity = state => get(state, "global.data.portfolioPositions.data.model.nonActive.composition[0].referenceEquity", 0)
const getWorkingEquity = createSelector(
  [getPortfolioEquity, getPortfolioGmv],
  (equity, gmv) => equity ?? gmv,
)

const getSimulationExperiment = createSelector(
  [getOldSimulationExperiment, getNewSimulationExperiment],
  (oldSimulationExperiment, newSimulationExperiment) => newSimulationExperiment || oldSimulationExperiment,
)

export const getPortfolioUUID = state => get(state, "global.data.portfolio.data.id")
export const getPortfolioAlias = (state) => get(state, "global.selected.portfolio")
export const getAccount = (state) => get(state, "global.selected.account")
export const getExperimentId = props => get(props, "params.experimentId", null)
export const getSelectedExperiment = state => get(state, "modules.editExperiment.experiment", null)
export const getIndex = state => get(state, "modules.editExperiment.index", null)
export const getStatus = state => get(state, "modules.editExperiment.status", null)

export const getExperimentsUrl = createSelector(
  [getPortfolioAlias, getAccount],
  (portfolioAlias, account) => `/${account}/p/${portfolioAlias}/rebalance/experiments`,
)

export const getExperimentToSave = createSelector(
  [getOptimizeExperiment, getSimulationExperiment, getSelectedExperiment],
  (optimizeExperiment, simulationExperiment, selectedExperiment) => {
    switch (selectedExperiment.type) {
      case "OPTIMIZATION":
        return optimizeExperiment
      case "SIMULATION":
        return simulationExperiment
    }
    return null
  },
)

const getExperimentPositionsWithRecombinedBaskets = createSelector(
  [getExperimentPositions],
  combinePositionsIncludingSplitBasket,
)

const getNewSimulationConfig = createSelector(
  [getSelectedExperiment],
  (experiment) => get(experiment, "config"),
)

export const getOldSimulationConfig = createSelector(
  [getSelectedExperiment, getExperimentPositionsWithRecombinedBaskets, getPortfolioPositions, getWorkingEquity, getLatestDate, getEarliestDate],
  (experiment, experimentPositions, portfolioPositions, workingEquity, portfolioLatestDate, portfolioEarliestDate) => {
    if (!experiment || !portfolioPositions) {
      return null
    }

    if (experiment.availableFrom > portfolioLatestDate || experiment.availableFrom < portfolioEarliestDate) {
      return null
    }

    const computedChanges = {
      addedPositions: {},
      changes: {},
    }

    if (experimentPositions) {
      computedChanges.changes = {
        ...chain(experimentPositions)
          .mapValues(experimentPosition => {
            const portfolioPosition = find(portfolioPositions, { id: experimentPosition.id })

            const experimentSecurityId = getTickerAndMicFromContributor({ assetClass: experimentPosition.assetClass, id: experimentPosition.id })
            const rebalancedPosition = {
              ...pick(experimentSecurityId, ["ticker", "mic"]),
              assetClass: experimentPosition.assetClass,
              size: experimentPosition.economicExposure / workingEquity,
            }

            if (!portfolioPosition) {
              const item = {
                ...pick(experimentSecurityId, ["ticker", "mic"]),
                ...pick(experimentPosition, ["id", "country", "assetClass", "modelProviderId"]),
              }
              set(computedChanges, ["addedPositions", buildContributorKey(item)], item)

              return rebalancedPosition
            }

            if (Math.abs((experimentPosition.economicExposure - portfolioPosition.economicExposure) / workingEquity) >= 0.00005) {
              return rebalancedPosition
            }

            return null
          })
          .filter(elem => elem)
          .mapKeys(formatPositionId)
          .value(),
        ...chain(portfolioPositions)
          .filter(portfolioPosition => !some(experimentPositions, experimentPosition => experimentPosition.id === portfolioPosition.id))
          .mapValues(portfolioOnlyPosition => {
            const securityId = getTickerAndMicFromContributor({ assetClass: portfolioOnlyPosition.assetClass, id: portfolioOnlyPosition.id })
            return { ticker: securityId.ticker, mic: securityId.mic, size: 0, assetClass: portfolioOnlyPosition.assetClass }
          })
          .mapKeys(formatPositionId)
          .value(),
      }
    }
    return computedChanges
  },
)

export const getSimulationConfig = createSelector(
  [getOldSimulationConfig, getNewSimulationConfig],
  (oldSimulationConfig, newSimulationConfig) => newSimulationConfig || oldSimulationConfig,
)

export const getHasChanges = createSelector(
  [getSimulateActions, getSimulationConfig, getExperimentToSave, getSelectedExperiment, getSelectedDate],
  (simulateActions, originalConfig, experimentToSave, selectedExperiment, selectedDate) => {
    const experimentType = get(selectedExperiment, "type")

    if (selectedDate !== selectedExperiment.availableFrom) {
      return true
    }

    if (experimentType === "SIMULATION") {
      const originalChanges = convertExperimentChangesToSimulateActions(get(originalConfig, "changes"))
      return !isEqual(simulateActions, originalChanges)
    } else if (experimentType === "OPTIMIZATION" && experimentToSave) {
      return !isEqual(selectedExperiment.config, experimentToSave.config)
    }

    return false
  },
)

// Even if the constraints configuration is the same, the optimizer might return a different positionSet
// Let the user decide if he want to update or not the optimization experiment.
export const getSaveEnabled = createSelector(
  [getExperimentToSave, getStatus],
  (experimentToSave, status) => !!experimentToSave && status === OK,
)

export const getIsOneDayExperiment = createSelector(
  [getSelectedDate, getLatestDate],
  (experimentSelectedDate, portfolioLatestDate) => experimentSelectedDate === portfolioLatestDate,
)
